import React from 'react';
import cx from 'classnames';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';

// Components
import { PlanAvailableIcon } from '../../Common/Assets/SvgIconComponent/CommonSvgIcon';
import CurrencyConverter from '../../CurrencyConverter/CurrencyConverter';
import { dateFormat } from '../../../config';
import { formatLocalizedDate } from '../../../helpers/formatLocateDate';
import { getPurchaseServicePlanValue } from '../../../helpers/GetDynamicItemName/dynamicHomeValue';

// Helpers / Config
import messages from '../../../locale/jsonMessages';
import s from './PurchasePlanReceiptInfo.css';

const PurchasePlanReceiptInfo = ({ data }) => {
    const { createdAt, PurchaserData, userType, updatedAt, servicePlanName, serviceDescription, dynamicServicePlanDetails } = data;
    const { serviceFee, serviceAIFeature, price, currency, duration, startDate, endDate } = data;

    const { formatMessage, locale } = useIntl();

    const createdDate = createdAt ? formatLocalizedDate(createdAt, locale, dateFormat?.fullDateFormat) : '';
    const updatedDate = updatedAt ? formatLocalizedDate(updatedAt, locale, dateFormat?.fullDateFormat) : '';
    const startingDate = startDate ? moment(parseInt(startDate)).format('L') : '';
    const endedDate = endDate ? moment(parseInt(endDate)).format('L') : '';
    const planDetails = JSON.parse(dynamicServicePlanDetails || '[]');
    const matchPlan = getPurchaseServicePlanValue(planDetails, locale)
    const planName = matchPlan?.planName ? matchPlan?.planName : servicePlanName;
    const description = matchPlan?.description ? matchPlan?.description : serviceDescription;

    return (
        <div className={s.purchasePlanReceiptBox}>
            <div className={cx(s.purchasePlanReceiptContainer, 'purchasePlanReceiptPrintBox')}>
                <div className={s.purchasePlanReceiptInfoBox}>
                    <div className={s.purchasePlanReceiptInfoHead}>
                        <div>
                            <h3 className={s.purchasePlanReceiptInfoTitle}>
                                <span className={s.purchasePlanReceiptLabel}><FormattedMessage {...messages.purchasedByLabel} />:&nbsp;</span>
                                <span className={s.purchasePlanReceiptLabel}>{PurchaserData?.firstName}</span>
                            </h3>
                            <div className={s.purchasePlanReceiptInfoDesc}>{createdDate}</div>
                        </div>
                        <div className={s.purchasePlanReceiptInfoBadge}>{userType === 'renter' ? formatMessage(messages.guest) : formatMessage(messages.ownerLabel)}</div>
                    </div>
                    <div className={s.purchasePlanReceiptPlanInfoBox}>
                        <h4 className={s.purchasePlanReceiptInfoSubTitle}>{planName}</h4>
                        <p className={cx(s.purchasePlanReceiptInfoDesc, s.purchasePlanReceiptPlanDesc)}>{description}</p>
                    </div>
                    <div className={s.purchasePlansReceiptBenefitsBox}>
                        <h4 className={s.purchasePlanReceiptInfoSubTitle}>{formatMessage(messages.benefitsLabel)}</h4>
                        {(serviceFee >= 0 || serviceAIFeature) && <div className={s.purchasePlanReceiptBenefitsList}>
                            {serviceFee >= 0 && (
                                <div className={s.purchasePlansReceiptBenefitsFeature}>
                                    <PlanAvailableIcon />
                                    <span className={s.purchasePlanReceiptInfoDesc}>
                                        <span>{serviceFee}%&nbsp;</span>
                                        <span>{formatMessage(messages.serviceFee)}</span>
                                    </span>
                                </div>
                            )}
                            {serviceAIFeature && (
                                <div className={s.purchasePlansReceiptBenefitsFeature}>
                                    <PlanAvailableIcon />
                                    <span className={s.purchasePlanReceiptInfoDesc}>
                                        <FormattedMessage {...messages.aiFeatureLabel} />
                                    </span>
                                </div>
                            )}
                        </div>}
                    </div>
                </div>
                <div className={s.purchasePlanReceiptPricingDetailsBox}>
                    <div className={s.purchasePlanReceiptBillingContainer}>
                        <h3 className={s.purchasePlanReceiptInfoTitle}><FormattedMessage {...messages.billing} /></h3>
                        <div className={s.purchasePlanReceiptPricingDetailsList}>
                            <div className={cx(s.purchasePlanReceiptInfoDesc, s.purchasePlanReceiptPricingDetailsItem)}>
                                <span className={s.purchasePlanReceiptPricingDetailsTitle}>{formatMessage(messages.priceLabel)}</span>
                                <span className={s.purchasePlanReceiptPricingDetailsAmount}><CurrencyConverter amount={price} from={currency} /></span>
                            </div>
                            <div className={cx(s.purchasePlanReceiptInfoDesc, s.purchasePlanReceiptPricingDetailsItem)}>
                                <span className={s.purchasePlanReceiptPricingDetailsTitle}>{formatMessage(messages.duration)}</span>
                                <span className={s.purchasePlanReceiptPricingDetailsAmount}>{duration}&nbsp;{duration == 1 ? formatMessage(messages.month) : formatMessage(messages.monthsLabel)}</span>
                            </div>
                            <div className={cx(s.purchasePlanReceiptInfoDesc, s.purchasePlanReceiptPricingDetailsItem)}>
                                <span className={s.purchasePlanReceiptPricingDetailsTitle}>{formatMessage(messages.StartDate)}</span>
                                <span className={s.purchasePlanReceiptPricingDetailsAmount}>{startingDate}</span>
                            </div>
                            <div className={cx(s.purchasePlanReceiptInfoDesc, s.purchasePlanReceiptPricingDetailsItem)}>
                                <span className={s.purchasePlanReceiptPricingDetailsTitle}>{formatMessage(messages.EndDate)}</span>
                                <span className={s.purchasePlanReceiptPricingDetailsAmount}>{endedDate}</span>
                            </div>
                        </div>
                    </div>
                    <div className={s.purchasePlanReceiptPaymentContainer}>
                        <h3 className={s.purchasePlanReceiptInfoTitle}><FormattedMessage {...messages.payment} /></h3>
                        <div className={s.purchasePlanReceiptPricingDetailsList}>
                            <div className={cx(s.purchasePlanReceiptInfoDesc, s.purchasePlanReceiptPricingDetailsItem)}>
                                <span className={s.purchasePlanReceiptPricingDetailsTitle}><FormattedMessage {...messages.paidLabel} /></span>
                                <span className={s.purchasePlanReceiptPricingDetailsAmount}><CurrencyConverter amount={price} from={currency} /></span>
                            </div>
                            <div className={s.purchasePlanReceiptInfoDesc}>{updatedDate}</div>
                        </div>
                    </div>
                </div>
            </div>
            <p className={s.purchasePlanReceiptBottomCaption}>
                {formatMessage(messages.theLabel)}&nbsp;
                <span className={s.purchasePlanReceiptSpecialText}>{formatMessage(messages.planPaymentMessage01)}&nbsp;</span>
                {formatMessage(messages.planPaymentMessage)}
            </p>
        </div>
    );
};

export default withStyles(s)(PurchasePlanReceiptInfo);
